import { useColorMode, useColorModeValue, useToken } from "@chakra-ui/react";
import { darken, hasBadContrast, lighten } from "color2k";
import { useMemo } from "react";

type Guidelines = "decorative" | "readable" | "aa" | "aaa";

/**
 * Adjusts a hex color to ensure sufficient contrast against a background
 * @param foregroundColor - The hex color to adjust
 * @param backgroundColor - The hex background color to contrast against
 * @param shouldBrighten - Whether to brighten the color
 * @param standard - The standard to use for the contrast ratio (defaults to "readable")
 * @returns The adjusted hex color that meets the contrast requirement
 */
function adjustColorForContrast(
  foregroundColor: string,
  backgroundColor: string,
  shouldBrighten: boolean,
  standard?: Guidelines,
): string {
  const guideline = standard ?? "decorative";
  if (!hasBadContrast(foregroundColor, guideline, backgroundColor)) {
    return foregroundColor;
  }

  // Use binary search to find the minimum adjustment needed
  let low = 0;
  let high = 1;
  let result = foregroundColor;
  const adjustFn = shouldBrighten ? lighten : darken;

  // Binary search for the minimum adjustment that meets contrast requirement
  for (let i = 0; i < 20; i++) {
    // 20 iterations gives us precision to ~0.0001
    const mid = (low + high) / 2;
    const adjusted = adjustFn(foregroundColor, mid);

    if (hasBadContrast(adjusted, guideline, backgroundColor)) {
      low = mid;
    } else {
      high = mid;
      result = adjusted;
    }
  }

  return result;
}

/**
 * Hook for adjusting colors to ensure sufficient contrast
 * @param foregroundColor - The color to adjust (Chakra token or hex color)
 * @param backgroundColor - The background color to contrast against (defaults to appBackground)
 * @param standard - The standard to use for the contrast ratio (defaults to "readable")
 * @returns The adjusted foreground color with sufficient contrast
 */
export function useColorContrast(
  foregroundColor: string,
  backgroundColor?: string,
  standard?: Guidelines,
): string {
  const defaultBackgroundColor = useColorModeValue("white", "gray.900");
  const { colorMode } = useColorMode();
  const resolvedForeground = useToken("colors", foregroundColor);
  const resolvedBackground = useToken(
    "colors",
    backgroundColor ?? defaultBackgroundColor,
  );

  return useMemo(
    () =>
      adjustColorForContrast(
        resolvedForeground,
        resolvedBackground,
        colorMode === "dark",
        standard,
      ),
    [resolvedForeground, resolvedBackground, colorMode, standard],
  );
}
